/**
 * CategoryLabel Component
 * A reusable component that handles category name translation using nameKey.
 * Falls back to raw name for custom categories without nameKey.
 */

import { useTranslation } from 'react-i18next';

interface CategoryLabelProps {
  /** The nameKey from the database (e.g., 'fixed_costs') */
  nameKey?: string | null;
  /** The raw category name as fallback */
  name: string;
  /** Optional className for styling */
  className?: string;
}

/**
 * Displays category name with i18n support.
 * Uses nameKey for translation lookup, falls back to raw name for custom categories.
 */
export function CategoryLabel({ nameKey, name, className }: CategoryLabelProps) {
  const { t } = useTranslation();

  // If nameKey exists, try to translate it
  if (nameKey) {
    const translationKey = `categories.${nameKey}`;
    const translated = t(translationKey);
    // If translation exists (not equal to the key), use it
    if (translated !== translationKey) {
      return <span className={className}>{translated}</span>;
    }
  }

  // Fallback to raw name for custom categories or if translation not found
  return <span className={className}>{name}</span>;
}

/**
 * Hook to get translated category name
 * Useful when you need the string value rather than a component
 */
export function useCategoryName(nameKey?: string | null, name?: string): string {
  const { t } = useTranslation();

  if (nameKey) {
    const translationKey = `categories.${nameKey}`;
    const translated = t(translationKey);
    if (translated !== translationKey) {
      return translated;
    }
  }

  return name || '';
}

/**
 * Get translated category name (non-hook version for use in callbacks)
 * Requires i18n t function as parameter
 */
export function getCategoryName(
  t: (key: string) => string,
  nameKey?: string | null,
  name?: string
): string {
  if (nameKey) {
    const translationKey = `categories.${nameKey}`;
    const translated = t(translationKey);
    if (translated !== translationKey) {
      return translated;
    }
  }

  return name || '';
}

export default CategoryLabel;
